import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  CloseOutlined,
  SearchOutlined,
  SendOutlined,
  PersonOutlined,
  CheckCircleOutlined,
  MailOutlined,
  GroupOutlined,
  ClearOutlined,
  AttachFileOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  DescriptionOutlined,
  TableChartOutlined,
  DeleteOutlined,
} from '@mui/icons-material';
import './BulkEmailModal.css';

// ─── helpers ────────────────────────────────────────────────────────────────
const ACCEPT_ALL = '.jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip';
const MAX_TOTAL_SIZE_MB = 25;   // limite : 25 Mo au total

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const getFileIcon = (file) => {
  const t = file.type;
  if (t.startsWith('image/'))             return <ImageOutlined style={{ fontSize: 18 }} />;
  if (t === 'application/pdf')            return <PictureAsPdfOutlined style={{ fontSize: 18 }} />;
  if (t.includes('spreadsheet') || t.includes('excel') || file.name.match(/\.xlsx?$/i))
                                          return <TableChartOutlined style={{ fontSize: 18 }} />;
  if (t.includes('word') || file.name.match(/\.docx?$/i))
                                          return <DescriptionOutlined style={{ fontSize: 18 }} />;
  return <InsertDriveFileOutlined style={{ fontSize: 18 }} />;
};

const getFileColor = (file) => {
  const t = file.type;
  if (t.startsWith('image/'))    return '#8b5cf6';
  if (t === 'application/pdf')   return '#ef4444';
  if (t.includes('spreadsheet') || t.includes('excel') || file.name.match(/\.xlsx?$/i)) return '#16a34a';
  if (t.includes('word') || file.name.match(/\.docx?$/i)) return '#2563eb';
  return '#6b7280';
};

// ─── AttachmentZone sub-component ────────────────────────────────────────────
const AttachmentZone = ({ attachments, onAdd, onRemove, error, setError }) => {
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const maxTotalBytes = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  const currentTotalSize = useMemo(
    () => attachments.reduce((sum, att) => sum + att.file.size, 0),
    [attachments]
  );

  const remainingBytes = maxTotalBytes - currentTotalSize;
  const canAddMore = remainingBytes > 0;

  const processFiles = useCallback((files) => {
    setError('');
    const incoming = Array.from(files);

    // Vérification taille totale uniquement
    const incomingTotalSize = incoming.reduce((sum, f) => sum + f.size, 0);
    if (currentTotalSize + incomingTotalSize > maxTotalBytes) {
      setError(`Limite totale de ${MAX_TOTAL_SIZE_MB} Mo dépassée. (${formatBytes(currentTotalSize)} / ${MAX_TOTAL_SIZE_MB} Mo utilisés)`);
      return;
    }

    const enriched = incoming.map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    onAdd(enriched);
  }, [currentTotalSize, maxTotalBytes, onAdd, setError]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFiles(e.dataTransfer.files);
  };

  const handleDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = ()    => setDragging(false);

  return (
    <div className="bem-attachment-zone">
      {/* Drop area */}
      {canAddMore && (
        <div
          className={`bem-drop-area ${dragging ? 'dragging' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ALL}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { processFiles(e.target.files); e.target.value = ''; }}
          />
          <div className="bem-drop-icons">
            <ImageOutlined />
            <AttachFileOutlined />
          </div>
          <span className="bem-drop-label">
            {dragging ? 'Déposez ici…' : 'Cliquez ou glissez des fichiers'}
          </span>
          <span className="bem-drop-hint">
            Tous types de fichiers · taille totale max {MAX_TOTAL_SIZE_MB} Mo
            {' · '}{formatBytes(remainingBytes)} restant{remainingBytes > 0 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Message si limite totale atteinte */}
      {!canAddMore && attachments.length > 0 && (
        <div className="bem-attach-limit-reached">
          Limite de {MAX_TOTAL_SIZE_MB} Mo atteinte. Supprimez des fichiers pour en ajouter d'autres.
        </div>
      )}

      {/* Error */}
      {error && <p className="bem-attach-error">{error}</p>}

      {/* Attachment list */}
      {attachments.length > 0 && (
        <div className="bem-attach-list">
          {attachments.map((att) => (
            <div key={att.id} className="bem-attach-item">
              {att.preview ? (
                <img src={att.preview} alt={att.file.name} className="bem-attach-thumb" />
              ) : (
                <div className="bem-attach-icon" style={{ color: getFileColor(att.file), background: `${getFileColor(att.file)}18` }}>
                  {getFileIcon(att.file)}
                </div>
              )}
              <div className="bem-attach-info">
                <span className="bem-attach-name">{att.file.name}</span>
                <span className="bem-attach-size">{formatBytes(att.file.size)}</span>
              </div>
              <button
                className="bem-attach-remove"
                onClick={() => onRemove(att.id)}
                title="Supprimer"
              >
                <DeleteOutlined style={{ fontSize: 16 }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const BulkEmailModal = ({
  isOpen,
  onClose,
  usersList = [],
  currentUser,
  onSendBulkEmail,
  isSending = false,
}) => {
  const [step, setStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState('');
  const [success, setSuccess] = useState(false);
  const textareaRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedIds(new Set());
      setSubject('');
      setMessage('');
      setAttachments([]);
      setAttachError('');
      setSearchTerm('');
      setSuccess(false);
    }
    // Revoke object URLs on close
    if (!isOpen) {
      attachments.forEach((a) => { if (a.preview) URL.revokeObjectURL(a.preview); });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return usersList.filter(
      (u) =>
        u.name?.toLowerCase().includes(term) ||
        u.lastname?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.username?.toLowerCase().includes(term)
    );
  }, [usersList, searchTerm]);

  const selectedUsers = useMemo(
    () => usersList.filter((u) => selectedIds.has(u.id)),
    [usersList, selectedIds]
  );

  const toggleUser = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map((u) => u.id)));
    }
  };

  const handleAddAttachments = useCallback((newAtts) => {
    setAttachments((prev) => [...prev, ...newAtts]);
  }, []);

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.preview) URL.revokeObjectURL(att.preview);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const totalAttachmentsSize = useMemo(
    () => attachments.reduce((sum, att) => sum + att.file.size, 0),
    [attachments]
  );

  const handleSend = async () => {
    if (!subject.trim() || !message.trim() || selectedIds.size === 0) return;
    try {
      const formData = new FormData();
      formData.append('user_ids', JSON.stringify([...selectedIds]));
      formData.append('subject', subject.trim());
      formData.append('message', message.trim());
      formData.append('sender_name', currentUser?.name || currentUser?.username || 'Admin');
      attachments.forEach((att) => formData.append('attachments', att.file));

      await onSendBulkEmail(formData);
      setSuccess(true);
      setTimeout(() => onClose(), 2400);
    } catch (e) {
      // handled upstream
    }
  };

  const canProceed = selectedIds.size > 0;
  const canSend    = subject.trim() && message.trim() && selectedIds.size > 0;

  if (!isOpen) return null;

  return (
    <div className="bem-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bem-modal">

        {/* ── Header ── */}
        <div className="bem-header">
          <div className="bem-header-left">
            <div className="bem-icon-wrap"><MailOutlined /></div>
            <div>
              <h2 className="bem-title">Messagerie groupée</h2>
              <p className="bem-subtitle">
                {step === 1
                  ? `${usersList.length} destinataires disponibles`
                  : `${selectedIds.size} destinataire${selectedIds.size > 1 ? 's' : ''} sélectionné${selectedIds.size > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <button className="bem-close" onClick={onClose}><CloseOutlined /></button>
        </div>

        {/* ── Steps ── */}
        <div className="bem-steps">
          <div
            className={`bem-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}
            onClick={() => step > 1 && setStep(1)}
          >
            <span className="bem-step-num">{step > 1 ? '✓' : '1'}</span>
            <span className="bem-step-label">Destinataires</span>
          </div>
          <div className="bem-step-line" />
          <div className={`bem-step ${step >= 2 ? 'active' : ''}`}>
            <span className="bem-step-num">2</span>
            <span className="bem-step-label">Rédiger</span>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="bem-body">

          {/* SUCCESS */}
          {success && (
            <div className="bem-success">
              <div className="bem-success-icon"><CheckCircleOutlined /></div>
              <h3>Message envoyé !</h3>
              <p>
                {selectedIds.size} destinataire{selectedIds.size > 1 ? 's' : ''} notifié{selectedIds.size > 1 ? 's' : ''}
                {attachments.length > 0 && ` · ${attachments.length} pièce${attachments.length > 1 ? 's' : ''} jointe${attachments.length > 1 ? 's' : ''} (${formatBytes(totalAttachmentsSize)})`}
              </p>
            </div>
          )}

          {/* STEP 1 — SELECT USERS */}
          {!success && step === 1 && (
            <div className="bem-step-content">
              <div className="bem-search-row">
                <div className="bem-search">
                  <SearchOutlined />
                  <input
                    type="text"
                    placeholder="Rechercher un utilisateur…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                  {searchTerm && (
                    <button className="bem-clear-search" onClick={() => setSearchTerm('')}>
                      <ClearOutlined style={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
                <button className="bem-select-all" onClick={toggleAll}>
                  <GroupOutlined style={{ fontSize: 16 }} />
                  {selectedIds.size === filteredUsers.length && filteredUsers.length > 0
                    ? 'Tout désélectionner'
                    : 'Tout sélectionner'}
                </button>
              </div>

              {selectedIds.size > 0 && (
                <div className="bem-chips">
                  {selectedUsers.slice(0, 6).map((u) => (
                    <span key={u.id} className="bem-chip">
                      <span>{u.name || u.username}</span>
                      <button onClick={() => toggleUser(u.id)}>
                        <CloseOutlined style={{ fontSize: 11 }} />
                      </button>
                    </span>
                  ))}
                  {selectedIds.size > 6 && (
                    <span className="bem-chip bem-chip-more">+{selectedIds.size - 6} autres</span>
                  )}
                </div>
              )}

              <div className="bem-user-list">
                {filteredUsers.length === 0 && (
                  <div className="bem-empty">
                    <PersonOutlined />
                    <span>Aucun utilisateur trouvé</span>
                  </div>
                )}
                {filteredUsers.map((user) => {
                  const isSelected = selectedIds.has(user.id);
                  const initials = `${user.name?.[0] || ''}${user.lastname?.[0] || ''}`
                    .toUpperCase() || user.username?.[0]?.toUpperCase() || '?';
                  return (
                    <div
                      key={user.id}
                      className={`bem-user-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleUser(user.id)}
                    >
                      <div className={`bem-user-avatar ${user.role}`}>{initials}</div>
                      <div className="bem-user-info">
                        <span className="bem-user-name">{user.name} {user.lastname}</span>
                        <span className="bem-user-email">{user.email}</span>
                      </div>
                      <span className={`bem-role-badge ${user.role}`}>{user.role}</span>
                      <div className={`bem-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <CheckCircleOutlined style={{ fontSize: 18 }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 2 — COMPOSE */}
          {!success && step === 2 && (
            <div className="bem-step-content bem-compose">

              {/* Recipients summary */}
              <div className="bem-recipients-summary">
                <div className="bem-rs-icon"><GroupOutlined /></div>
                <div className="bem-rs-text">
                  <span className="bem-rs-count">
                    {selectedIds.size} destinataire{selectedIds.size > 1 ? 's' : ''}
                  </span>
                  <span className="bem-rs-names">
                    {selectedUsers.slice(0, 3).map((u) => u.name || u.username).join(', ')}
                    {selectedIds.size > 3 ? ` et ${selectedIds.size - 3} autres` : ''}
                  </span>
                </div>
                <button className="bem-rs-edit" onClick={() => setStep(1)}>Modifier</button>
              </div>

              {/* Sender */}
              <div className="bem-field">
                <label className="bem-label">Expéditeur</label>
                <div className="bem-sender-display">
                  <PersonOutlined style={{ fontSize: 16 }} />
                  {currentUser?.username || 'Admin'}
                </div>
              </div>

              {/* Subject */}
              <div className="bem-field">
                <label className="bem-label">Objet <span className="req">*</span></label>
                <input
                  className="bem-input"
                  type="text"
                  placeholder="Objet de l'email…"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  autoFocus
                />
              </div>

              {/* Message */}
              <div className="bem-field bem-field-grow">
                <label className="bem-label">Message <span className="req">*</span></label>
                <textarea
                  ref={textareaRef}
                  className="bem-textarea"
                  placeholder="Rédigez votre message ici…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                />
                <span className="bem-char-count">{message.length} caractères</span>
              </div>

              {/* Attachments */}
              <div className="bem-field">
                <label className="bem-label">
                  Pièces jointes
                  {attachments.length > 0 && (
                    <span className="bem-attach-count-badge">
                      {formatBytes(totalAttachmentsSize)} / {MAX_TOTAL_SIZE_MB} Mo
                    </span>
                  )}
                </label>
                <AttachmentZone
                  attachments={attachments}
                  onAdd={handleAddAttachments}
                  onRemove={handleRemoveAttachment}
                  error={attachError}
                  setError={setAttachError}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {!success && (
          <div className="bem-footer">
            {step === 1 ? (
              <>
                <button className="bem-btn-ghost" onClick={onClose}>Annuler</button>
                <button
                  className={`bem-btn-primary ${!canProceed ? 'disabled' : ''}`}
                  onClick={() => canProceed && setStep(2)}
                  disabled={!canProceed}
                >
                  Continuer
                  {selectedIds.size > 0 && (
                    <span className="bem-btn-badge">{selectedIds.size}</span>
                  )}
                </button>
              </>
            ) : (
              <>
                <button className="bem-btn-ghost" onClick={() => setStep(1)}>← Retour</button>
                <button
                  className={`bem-btn-send ${!canSend || isSending ? 'disabled' : ''}`}
                  onClick={handleSend}
                  disabled={!canSend || isSending}
                >
                  {isSending ? <span className="bem-spinner" /> : <SendOutlined style={{ fontSize: 16 }} />}
                  {isSending
                    ? 'Envoi en cours…'
                    : `Envoyer à ${selectedIds.size} personne${selectedIds.size > 1 ? 's' : ''}`}
                  {attachments.length > 0 && !isSending && (
                    <span className="bem-send-attach-hint">
                      <AttachFileOutlined style={{ fontSize: 13 }} />
                      {attachments.length}
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkEmailModal;